import React, { useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Dialog, DialogBody, DialogTitle } from '../common/Catalyst/dialog';
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '../common/Catalyst/fieldset';
import { Button } from '../common/Catalyst/button';
import { HoaReturn, HomeReturn, MemberReturn } from '../clients/rust-api-client';
import { Divider } from '../common/Catalyst/divider';
import { FieldPicker } from '../common/Custom/FieldPicker';
import { hoaService, homeService } from '../clients/ApiClients';
import LoadingSpinner from '../LoadingSpinner';
import {
  getUserTypeFromToken,
  getCompanyIdFromToken,
} from '../utils/TokenUtils';
import { UserType } from '../constants/UserTypes';
import { useSyncedHoas, useSyncedHomes } from '../sync/hooks';

interface AddHomeToMemberDialogProps {
  isOpen: boolean;
  member: MemberReturn;
  onClose: () => void;
  onSave: (addHometoMember: AddHomeToMember) => Promise<void>;
}

interface AddHomeToMember {
  homeId: string;
  memberId: string;
}

interface DropdownHome extends HomeReturn {
  name: string;
}

// Transform the home data to match the BaseOption interface
const toOption = (home: HomeReturn): DropdownHome => ({
  ...home,
  id: home.id,
  name: home.street + home.street2 || `Home ${home.id}`,
});

export const AddHomeToMemberDialog: React.FC<AddHomeToMemberDialogProps> = ({
  isOpen,
  member,
  onClose,
  onSave,
}) => {
  const [fetchedHoaOptions, setHoaOptions] = useState<HoaReturn[]>([]);
  const [fetchedHomeOptions, setHomeOptions] = useState<DropdownHome[]>([]);
  const [selectedHoa, setSelectedHoa] = useState<HoaReturn | null>(null);
  // Associations and their homes from the sync engine's copy when there
  // is one (#561); fetched otherwise.
  const syncedHoas = useSyncedHoas();
  const haveSynced = syncedHoas !== null;
  const hoaOptions = syncedHoas ?? fetchedHoaOptions;
  const syncedHomes = useSyncedHomes({ hoaId: selectedHoa?.id });
  const syncedHomeOptions = useMemo(
    () => (selectedHoa && syncedHomes ? syncedHomes.map(toOption) : null),
    [selectedHoa, syncedHomes]
  );
  const homeOptions = syncedHomeOptions ?? fetchedHomeOptions;
  const [selectedHome, setSelectedHome] = useState<HomeReturn | null>(null);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  useEffect(() => {
    const fetchHoas = async () => {
      if (haveSynced) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const userType = getUserTypeFromToken();
        const companyId = getCompanyIdFromToken();
        // SuperAdmin can view all HOAs, others can only view their company's HOAs
        const response =
          userType === UserType.SUPER_ADMIN
            ? await hoaService.getAllHoas()
            : await hoaService.getHoasByCompany({ companyId: companyId || '' });
        setHoaOptions(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError("Couldn't load HOAs. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchHoas();
  }, [haveSynced]);

  const handleHoaSelected = async (hoa: HoaReturn | null) => {
    setSelectedHoa(hoa);
    setSelectedHome(null);
    setHomeOptions([]);

    if (hoa && !haveSynced) {
      try {
        setLoading(true);
        const response = await homeService.getHomesByHoa({
          hoaId: hoa.id,
        });
        setHomeOptions(response.data.map(toOption));
        setError(null);
      } catch (err) {
        console.error('Error fetching homes:', err);
        setError("Couldn't load homes. Please try again later.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSave = async () => {
    if (isValid) {
      try {
        setLoading(true);
        await onSave({ memberId: member.id, homeId: selectedHome!.id });
        onClose();
      } catch (err) {
        console.error('Error saving home to member:', err);
        setError("Couldn't save. Please try again later.");
      } finally {
        setLoading(false);
      }
    }
  };

  const isValid = selectedHome && selectedHoa && member;

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        className="w-full max-w-2xl mx-auto"
      >
        <div className="relative flex flex-col h-full">
          <div className="p-4 md:p-6">
            <DialogTitle className="text-xl font-semibold">
              Add a Home to Member
            </DialogTitle>
            <button
              onClick={onClose}
              className="absolute right-4 top-4 p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-full transition-colors duration-150"
              aria-label="Close dialog"
            >
              <XMarkIcon className="h-5 w-5 text-zinc-600 dark:text-zinc-100" />
            </button>

            <Divider className="my-4" />

            {loading && <LoadingSpinner></LoadingSpinner>}

            <DialogBody>
              <Fieldset>
                <FieldGroup>
                  {/*HOA Selector */}

                  <div className="space-y-4">
                    <Label htmlFor="hoa">Select an HOA*</Label>
                    <Field>
                      <FieldPicker
                        label="HOA"
                        options={hoaOptions.map((hoa) => ({
                          value: String(hoa.id),
                          label: hoa.name,
                        }))}
                        value={selectedHoa ? String(selectedHoa.id) : null}
                        onChange={(value) =>
                          handleHoaSelected(
                            hoaOptions.find((hoa) => String(hoa.id) === value) ??
                              null
                          )
                        }
                        placeholder="Select an HOA"
                        filterPlaceholder="Search for an HOA..."
                      />
                    </Field>

                    {/*Honme Selector */}
                    <Field>
                      <Label htmlFor="home">Select a Home*</Label>
                      <FieldPicker
                        label="Home"
                        options={homeOptions.map((home) => ({
                          value: String(home.id),
                          label: home.name,
                        }))}
                        value={selectedHome ? String(selectedHome.id) : null}
                        onChange={(value) =>
                          setSelectedHome(
                            homeOptions.find(
                              (home) => String(home.id) === value
                            ) ?? null
                          )
                        }
                        placeholder="Select a Home"
                        filterPlaceholder="Search for a Home..."
                      />
                    </Field>
                  </div>
                </FieldGroup>
              </Fieldset>
            </DialogBody>
          </div>
          <div className="sticky bottom-0 mt-auto border-t border-gray-200 dark:border-gray-700 bg-stone-100 dark:bg-gray-800 p-4 md:p-6">
            <div className="flex flex-col space-y-3">
              <Button
                onClick={handleSave}
                color="sky"
                disabled={!isValid}
                className="w-full disabled:opacity-50"
              >
                Save
              </Button>
              <Button onClick={onClose} color="zinc" className="w-full">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AddHomeToMemberDialog;
